import { useCallback, useEffect, useState } from 'react';

import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';

const DATABASE_NAME = 'aztec-realms-offline';
const DATABASE_VERSION = 1;
const STORE_NAME = 'realm-snapshots';
const SNAPSHOT_KEY = 'latest';
const SNAPSHOT_EVENT = 'aztec-realms:offline-snapshot-changed';

export interface OfflineRealmSnapshot {
  version: 1;
  cachedAt: string;
  profile: {
    displayName: string;
    level: number;
  };
  settlement: {
    name: string;
    level: number;
    visualTier: number;
    biome: string;
    buildings: readonly {
      label: string;
      level: number;
      state: string;
    }[];
  };
  codexFamilies: readonly {
    label: string;
    decodedCount: number;
    totalCount: number;
    masteryTier: string;
    entries: readonly {
      title: string;
      status: string;
      reviewStatus: string;
    }[];
  }[];
}

function boundedText(value: string, maximumLength: number): string {
  return value.trim().slice(0, maximumLength);
}

/**
 * Build a deliberately narrow presentation snapshot. Economy balances, rewards,
 * exact locations, discovery identifiers, puzzle state and session identifiers
 * are excluded so this cache can never become a second source of game truth.
 */
export function createOfflineRealmSnapshot(
  view: PlayerVerticalSliceView,
  cachedAt = new Date().toISOString(),
): OfflineRealmSnapshot {
  return {
    version: 1,
    cachedAt,
    profile: {
      displayName: boundedText(view.profile.displayName, 64),
      level: view.profile.level,
    },
    settlement: {
      name: boundedText(view.settlement.name, 64),
      level: view.settlement.level,
      visualTier: view.settlement.visualTier,
      biome: boundedText(view.settlement.biome, 64),
      buildings: view.settlement.buildings.map((building) => ({
        label: boundedText(building.label, 64),
        level: building.level,
        state: building.state,
      })),
    },
    codexFamilies: view.codexFamilies.map((family) => ({
      label: boundedText(family.label, 64),
      decodedCount: family.decodedCount,
      totalCount: family.totalCount,
      masteryTier: family.masteryTier,
      entries: family.entries.map((entry) => ({
        title: boundedText(entry.title ?? 'Unknown sign', 96),
        status: entry.status,
        reviewStatus: entry.reviewStatus,
      })),
    })),
  };
}

function openDatabase(): Promise<IDBDatabase | undefined> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(undefined);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Offline storage could not open.'));
    request.onblocked = () => resolve(undefined);
  });
}

export async function deleteOfflineRealmDatabase(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('Offline storage could not clear.'));
    request.onblocked = () => reject(new Error('Offline storage is still in use.'));
  });
  window.dispatchEvent(new Event(SNAPSHOT_EVENT));
}

async function transact<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | undefined> {
  const database = await openDatabase();
  if (!database) return undefined;
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Offline storage failed.'));
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('Offline storage aborted.'));
    });
  } finally {
    database.close();
  }
}

function validSnapshot(value: unknown): value is OfflineRealmSnapshot {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<OfflineRealmSnapshot>;
  return (
    candidate.version === 1 &&
    typeof candidate.cachedAt === 'string' &&
    typeof candidate.profile?.displayName === 'string' &&
    typeof candidate.settlement?.name === 'string' &&
    Array.isArray(candidate.settlement.buildings) &&
    Array.isArray(candidate.codexFamilies)
  );
}

export async function saveOfflineRealmSnapshot(view: PlayerVerticalSliceView): Promise<void> {
  // Local fixtures are intentionally never persisted as if they were a player's server state.
  if (view.source !== 'api') return;
  await transact('readwrite', (store) => store.put(createOfflineRealmSnapshot(view), SNAPSHOT_KEY));
  window.dispatchEvent(new Event(SNAPSHOT_EVENT));
}

export async function loadOfflineRealmSnapshot(): Promise<OfflineRealmSnapshot | undefined> {
  const value = await transact<unknown>('readonly', (store) => store.get(SNAPSHOT_KEY));
  return validSnapshot(value) ? value : undefined;
}

export async function clearOfflineRealmSnapshot(): Promise<void> {
  await transact('readwrite', (store) => store.delete(SNAPSHOT_KEY));
  window.dispatchEvent(new Event(SNAPSHOT_EVENT));
}

export function useOfflineRealmSnapshot(enabled: boolean) {
  const [snapshot, setSnapshot] = useState<OfflineRealmSnapshot>();
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      setSnapshot(await loadOfflineRealmSnapshot());
    } catch {
      // Offline storage is a progressive enhancement; its failure must not hide the shell.
      setSnapshot(undefined);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const initialRefresh = window.setTimeout(() => void refresh(), 0);
    window.addEventListener(SNAPSHOT_EVENT, refresh);
    return () => {
      window.clearTimeout(initialRefresh);
      window.removeEventListener(SNAPSHOT_EVENT, refresh);
    };
  }, [enabled, refresh]);

  return { snapshot, loading: enabled && loading, refresh };
}
